/** TanStack Query hooks of the catalogue-sync page. Every write invalidates
 *  `['catalogue', tenantId]` — rows, the open product, the review queue, runs and the badge. */
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import * as catalogueApi from '@/api/catalogue';
import {
  catalogueKeys,
  type CatalogueRow,
  type CatalogueRowParams,
  type CatalogueScope,
  type SettingsPatch,
} from '@/api/catalogue';
import { ConsoleApiError } from '@/api/client';

import type { SyncScope } from '../SyncScopePicker';

/** The catalogue scope of a sync scope, or null until tenant + integration are chosen. */
export function catalogueScopeOf(scope: SyncScope): CatalogueScope | null {
  if (!scope.tenant || !scope.integration) return null;
  return {
    tenantId: scope.tenant.id,
    integration: scope.integration,
    account: scope.account,
    tenantCode: scope.tenant.code ?? null,
  };
}

/** A 4xx is an answer, not an outage: never retry it. */
function retryUnlessAnswered(count: number, e: Error): boolean {
  if (e instanceof ConsoleApiError && e.status >= 400 && e.status < 500) return false;
  return count < 2;
}

/** The settings registry of the scope's restech. A restech without catalogue settings answers
 *  400 `unsupported_integration`: that is how the page knows it keeps no catalogue rows, so
 *  nothing here names an integration. */
export function useCatalogueRegistry(scope: CatalogueScope | null) {
  return useQuery({
    queryKey: scope
      ? catalogueKeys.registry(scope.tenantId, scope.integration)
      : ['catalogue', 'registry', 'none'],
    queryFn: ({ signal }) => catalogueApi.getRegistry(scope!.tenantId, scope!.integration, signal),
    enabled: scope !== null,
    staleTime: 10 * 60_000,
    retry: retryUnlessAnswered,
  });
}

export function useCatalogueRows(scope: CatalogueScope | null, params: CatalogueRowParams) {
  return useQuery({
    queryKey: scope ? catalogueKeys.rows(scope, params) : ['catalogue', 'rows', 'none'],
    queryFn: ({ signal }) => catalogueApi.listRows(scope!, params, signal),
    enabled: scope !== null,
    placeholderData: keepPreviousData,
    retry: retryUnlessAnswered,
  });
}

export function useCatalogueProduct(tenantId: number, productId: number | null) {
  return useQuery({
    queryKey: catalogueKeys.product(tenantId, productId ?? 0),
    queryFn: ({ signal }) => catalogueApi.getProduct(tenantId, productId!, signal),
    enabled: productId !== null,
    retry: retryUnlessAnswered,
  });
}

/** The query of one product by its vendor id: the rows endpoint's `search` matches vendor ids by
 *  substring, so the exact one is picked from what it returns (null when there is none). */
function productRefQuery(scope: CatalogueScope | null, vendorId: string | null) {
  return {
    queryKey:
      scope && vendorId ? catalogueKeys.productRef(scope, vendorId) : ['catalogue', 'ref', 'none'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const page = await catalogueApi.listRows(
        scope!,
        { search: vendorId!, page: 1, page_size: PRODUCT_REF_PAGE },
        signal,
      );
      return page.results.find((r) => r.vendor_id === vendorId) ?? null;
    },
    enabled: scope !== null && Boolean(vendorId),
    staleTime: 5 * 60_000,
    retry: retryUnlessAnswered,
  };
}

/** One product of the scope by its vendor id (a `catalogue_product` setting's value), or null when
 *  the catalogue has no such product. */
export function useCatalogueProductRef(scope: CatalogueScope | null, vendorId: string | null) {
  return useQuery(productRefQuery(scope, vendorId));
}

/** Several products of the scope by vendor id (`productRef` each, shared with
 *  `useCatalogueProductRef`'s cache): vendor id -> its row, null when the catalogue has none, and
 *  absent while it loads. For a handful of ids only — a product's joiners named in its drawer. */
export function useCatalogueProductRefs(
  scope: CatalogueScope | null,
  vendorIds: readonly string[],
): Map<string, CatalogueRow | null> {
  const results = useQueries({ queries: vendorIds.map((v) => productRefQuery(scope, v)) });
  const out = new Map<string, CatalogueRow | null>();
  vendorIds.forEach((v, i) => {
    const data = results[i]?.data;
    if (data !== undefined) out.set(v, data);
  });
  return out;
}

/** How many rows a vendor-id lookup reads (substring matches of the id). */
const PRODUCT_REF_PAGE = 20;

/** The review queue's size (the page header badge): one row is enough to read `count`. */
export function usePendingReviewCount(scope: CatalogueScope | null, enabled: boolean) {
  return useQuery({
    queryKey: scope ? catalogueKeys.review(scope, { badge: true }) : ['catalogue', 'review', 'none'],
    queryFn: ({ signal }) => catalogueApi.listReview(scope!, { page: 1, page_size: 1 }, signal),
    enabled: scope !== null && enabled,
    select: (page) => page.count,
    retry: retryUnlessAnswered,
  });
}

export function useInvalidateCatalogue(tenantId: number | undefined) {
  const qc = useQueryClient();
  return useCallback(() => {
    if (tenantId === undefined) return Promise.resolve();
    return qc.invalidateQueries({ queryKey: catalogueKeys.all(tenantId) });
  }, [qc, tenantId]);
}

/** PATCH one product's settings (several keys, all or nothing). */
export function usePatchProductSettings(tenantId: number) {
  const invalidate = useInvalidateCatalogue(tenantId);
  return useMutation({
    mutationFn: (vars: { productId: number; body: SettingsPatch }) =>
      catalogueApi.patchProductSettings(tenantId, vars.productId, vars.body),
    onSuccess: () => invalidate(),
  });
}
